"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "util/cn"
import { usePortalLayer } from "ui/layers"

// Context to support filterable/autocomplete behavior
interface SelectContextValue {
  filterable: boolean
  search: string
  setSearch: React.Dispatch<React.SetStateAction<string>>
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}
const SelectContext = React.createContext<SelectContextValue>({
  filterable: false,
  search: "",
  setSearch: () => {},
  open: false,
  setOpen: () => {},
})

// Extend Radix Select props to include filterable flag
interface SelectProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> {
  filterable?: boolean
}

// Custom Select component wrapping Radix Select.Root
const Select: React.FC<SelectProps> = ({ filterable = false, children, ...props }) => {
  const [search, setSearch] = React.useState("")
  const [open, setOpen] = React.useState(false)

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!open) setSearch("")
  }, [open])

  return (
    <SelectContext.Provider value={{ filterable, search, setSearch, open, setOpen }}>
      <SelectPrimitive.Root open={open} onOpenChange={setOpen} {...props}>
        {children}
      </SelectPrimitive.Root>
    </SelectContext.Provider>
  )
}

// Other parts unchanged: Group, Value, Separator, Scroll buttons...
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

interface SelectContentProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> {
  layerBase?: number
}

// Trigger: renders as input when filterable, otherwise default button
const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { filterable?: boolean }
>(({ className, children, ...props }, ref) => {
  const { filterable, search, setSearch, setOpen } = React.useContext(SelectContext)
  if (filterable) {
    // Extract placeholder from SelectValue child
    let placeholder = ""
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === SelectPrimitive.Value) {
        placeholder = (child.props as any).placeholder || ""
      }
    })
    return (
      <SelectPrimitive.Trigger ref={ref} asChild {...props}>
        <div
          className={cn(
            `relative group flex items-center select-none justify-between whitespace-break-spaces shadow-sm disabled:cursor-not-allowed disabled:opacity-50`,
            className
          )}
          onClick={() => setOpen(true)}
        >
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full bg-transparent outline-none"
          />
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="entity-filter-select-indicator shrink-0 ml-2 size-4 transition-transform duration-200 ease-in-out group-data-[state=open]:rotate-180" />
          </SelectPrimitive.Icon>
        </div>
      </SelectPrimitive.Trigger>
    )
  }
  // Default trigger
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        `group entity-filter-select flex items-center select-none justify-between whitespace-break-spaces shadow-sm disabled:cursor-not-allowed disabled:opacity-50`,
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="entity-filter-select-indicator shrink-0 ml-2 size-4 transition-transform duration-200 ease-in-out group-data-[state=open]:rotate-180" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

// Content: filters items based on search when filterable
const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>(({ className, children, position = "popper", layerBase, style, ...props }, ref) => {
  const { filterable, search } = React.useContext(SelectContext)
  const portalLayer = usePortalLayer(layerBase)
  const filtered = React.Children.toArray(children).filter((child: any) => {
    if (!filterable) return true
    const val = String(child.props.value || "")
    const text = typeof child.props.children === "string" ? child.props.children : ""
    return (
      val.toLowerCase().includes(search.toLowerCase()) ||
      text.toLowerCase().includes(search.toLowerCase())
    )
  })
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative max-h-[300px] min-w-[8rem] max-w-[calc(100svw-1rem)] whitespace-break-spaces text-wrap rounded-md bg-eoe-bg dark:bg-eoe-dark-bg-hover placeholder:text-md shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        style={{ ...(style ?? {}), zIndex: portalLayer }}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[calc(var(--radix-select-trigger-height) - 300px)] w-full min-w-[var(--radix-select-trigger-width)]"
          )}
        >
          {filtered}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

// Rest of components unchanged
const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold text-eoe-text-sub dark:text-eoe-dark-text-sub bg-eoe-icon-hover dark:bg-eoe-dark-icon-hover", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "entity-filter-select-options relative flex w-full select-none whitespace-break-spaces text-wrap items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText className="text-wrap whitespace-break-spaces">{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-eoe-border-main dark:bg-eoe-dark-border-main", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-pointer items-center justify-center py-1 hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover border-b border-eoe-border-main dark:border-eoe-dark-border-main",
      className
    )}
    {...props}
  >
    <ChevronUp className="entity-filter-select-indicator size-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-pointer items-center justify-center py-1 hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover border-t border-eoe-border-main dark:border-eoe-dark-border-main",
      className
    )}
    {...props}
  >
    <ChevronDown className="entity-filter-select-indicator size-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
